import React, { useState } from 'react';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  ListItemIcon,
  useMediaQuery,
  Badge,
  Tooltip,
  useScrollTrigger,
  alpha,
  Zoom,
  InputBase,
  styled
} from '@mui/material';
import {
  Menu as MenuIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  AdminPanelSettings as AdminIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  Search as SearchIcon,
  Close as CloseIcon,
  BarChart as AnalyticsIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeContext } from '../../contexts/ThemeContext';
import { useSectionSafe, SECTIONS } from '../../contexts/SectionContext';
import { 
  mainNavigationItems, 
  isActiveRoute, 
  filterNavigationItems
} from '../../constants/navigation';
import { BRAND, UI_TEXT } from '../../constants/branding';
import NotificationMenu from '../notifications/NotificationMenu';
import MessagesMenu from '../messages/MessagesMenu';
import { useAuthModal } from '../../hooks/useAuthModal';
import LocationIndicator from '../common/LocationIndicator';
import SectionTabs from './SectionTabs';


const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  width: '100%',
  height: '100%',
  '& .MuiInputBase-root': {
    outline: 'none !important',
    boxShadow: 'none !important',
  },
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 2, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    fontSize: '14px',
    outline: 'none !important',
    boxShadow: 'none !important',
    '&:focus, &:focus-visible': {
      outline: 'none !important',
      boxShadow: 'none !important',
    },
    [theme.breakpoints.up('md')]: {
      width: '35ch',
    },
  },
}));


interface HeaderProps {
  mobileDrawerOpen: boolean;
  setMobileDrawerOpen: (open: boolean) => void;
  elevated?: boolean;
}

const Header: React.FC<HeaderProps> = ({ 
  mobileDrawerOpen, 
  setMobileDrawerOpen, 
  elevated = false 
}) => {
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [messagesCount, setMessagesCount] = useState(0);
  
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentTheme, mode, toggleTheme } = useThemeContext();
  const { openLogin, openRegister } = useAuthModal();
  
  // Section tabs - safely get context (may be null outside SectionProvider)
  const sectionContext = useSectionSafe();
  
  const isMobile = useMediaQuery(currentTheme.breakpoints.down('md'));
  const isSmall = useMediaQuery(currentTheme.breakpoints.down('sm'));
  
  const isDarkMode = mode === 'dark';
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 10,
  });

  // Check if we're on Everything Else route
  const isEverythingElseRoute = location.pathname.startsWith('/everything');

  // Filter navigation items based on auth status AND section
  const visibleNavItems = React.useMemo(() => {
    const filtered = filterNavigationItems(
      mainNavigationItems, 
      !!user, 
      user?.isAdmin ? ['admin'] : undefined
    );
    
    // Hide automotive-specific items on Everything Else routes
    if (isEverythingElseRoute) {
      return filtered.filter(item => 
        !['explore', 'browse', 'categories', 'create-post'].includes(item.id || '')
      );
    }
    
    return filtered;
  }, [user, isEverythingElseRoute]);

  // Fetch unread messages count
  const fetchUnreadCount = React.useCallback(async () => {
    if (user) {
      try {
        const API_BASE = process.env.NEXT_PUBLIC_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const response = await fetch(`${API_BASE}/api/messages/unread/count`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setMessagesCount(data.data.count || 0);
        }
      } catch (error) {
        console.error('Failed to fetch unread messages count:', error);
      }
    }
  }, [user]);

  React.useEffect(() => {
    fetchUnreadCount();
    // Refresh every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Listen for global refresh events from messages page
  React.useEffect(() => {
    const handler = () => fetchUnreadCount();
    // @ts-ignore - allow custom event
    window.addEventListener('messages:refreshUnread', handler);
    return () => {
      // @ts-ignore
      window.removeEventListener('messages:refreshUnread', handler);
    };
  }, [fetchUnreadCount]);

  // Refresh unread count when navigating away from messages page
  React.useEffect(() => {
    if (!location.pathname.includes('/messages')) {
      // Small delay to allow API calls to complete
      const timer = setTimeout(() => fetchUnreadCount(), 300);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, fetchUnreadCount]);

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchValue.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchValue)}`);
      setSearchValue('');
      setSearchOpen(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      handleUserMenuClose();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
      <AppBar 
        position="fixed" 
        color="default"
        elevation={trigger || elevated ? 4 : 1}
        sx={{ 
          zIndex: 1300, // Set very high z-index to ensure header stays on top
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          backdropFilter: 'blur(8px)',
          backgroundColor: (theme) => alpha(theme.palette.background.default, 0.95),
          transition: 'box-shadow 0.3s ease, background-color 0.3s ease'
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 48, sm: 56, md: 64 }, px: { xs: 1, sm: 2, md: 3 } }}>
          {/* Mobile menu button */}
          {isMobile && (
            <IconButton
              size="small"
              edge="start"
              color="inherit"
              aria-label="Open navigation menu"
              onClick={() => setMobileDrawerOpen(!mobileDrawerOpen)}
              sx={{ mr: { xs: 0.5, sm: 2 } }}
            >
              <MenuIcon sx={{ fontSize: { xs: 22, sm: 24 } }} />
            </IconButton>
          )}

          {/* Dawr Logo */}
          <Box
            component={RouterLink}
            to="/"
            aria-label="Dawr - Go to home page"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              textDecoration: 'none',
              color: 'text.primary',
              mr: 3,
              flexShrink: 0,
            }}
          >
            {isSmall ? (
              <Box
                component="img"
                src="/favicon.svg"
                alt="Dawr"
                sx={{ width: 32, height: 32 }}
              />
            ) : (
              <>
                <Box
                  component="img"
                  src="/favicon.svg"
                  alt="Dawr icon"
                  sx={{ width: 40, height: 40 }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #1E6FA8 0%, #14B8A6 100%)',
                    backgroundClip: 'text',
                    color: 'transparent',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {BRAND.name}
                </Typography>
              </>
            )}
          </Box>

          {/* Section Tabs - Inline between logo and search */}
          {sectionContext && !isMobile && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mr: 2,
              }}
            >
              {Object.values(SECTIONS).map((section) => (
                <Box
                  key={section.id}
                  onClick={() => sectionContext?.setSection(section.id)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    backgroundColor: sectionContext?.currentSection === section.id
                      ? (theme) => alpha(theme.palette.primary.main, 0.12)
                      : 'transparent',
                    border: sectionContext?.currentSection === section.id
                      ? (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                      : '1px solid transparent',
                    '&:hover': {
                      backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                    },
                  }}
                >
                  <Typography component="span" sx={{ fontSize: '1rem' }}>
                    {section.icon}
                  </Typography>
                  <Box>
                    <Typography
                      component="span"
                      sx={{
                        fontWeight: sectionContext?.currentSection === section.id ? 600 : 500,
                        fontSize: '0.85rem',
                        // Explicit dark shade, not the raw 'primary.main' token: at
                        // this small (non-"large text") size the active pill's
                        // primary.main text against its own alpha(primary.main, 0.12)
                        // background (both derived from the same hue) only reaches
                        // 3.08:1 contrast — below the 4.5:1 required. #0369a1 (used
                        // elsewhere against plain white) still only reaches 4.46:1
                        // here against this tinted background, so go one shade darker.
                        color: sectionContext?.currentSection === section.id
                          ? '#075985'
                          : 'text.primary',
                        display: 'block',
                        lineHeight: 1.2,
                      }}
                    >
                      {section.label}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: '0.65rem',
                        color: 'text.secondary',
                        display: 'block',
                        lineHeight: 1.2,
                      }}
                    >
                      {section.description}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {/* Search bar - Hidden on Everything Else (has its own search) */}
          {!isEverythingElseRoute && (!isMobile || searchOpen) && (
            <Box component="form" onSubmit={handleSearch} sx={{
              position: 'relative',
              borderRadius: '24px',
              backgroundColor: theme => isDarkMode 
                ? alpha(theme.palette.common.white, 0.08)
                : alpha(theme.palette.common.black, 0.05),
              border: theme => `1px solid ${isDarkMode 
                ? alpha(theme.palette.common.white, 0.1)
                : alpha(theme.palette.common.black, 0.1)}`,
              outline: 'none !important',
              boxShadow: 'none !important',
              '&:hover': {
                backgroundColor: theme => isDarkMode
                  ? alpha(theme.palette.common.white, 0.12)
                  : alpha(theme.palette.common.black, 0.08),
                border: theme => `1px solid ${isDarkMode 
                  ? alpha(theme.palette.common.white, 0.15)
                  : alpha(theme.palette.common.black, 0.15)}`,
              },
              '&:focus, &:focus-within, &:focus-visible': {
                outline: 'none !important',
                boxShadow: 'none !important',
                border: theme => `1px solid ${isDarkMode 
                  ? alpha(theme.palette.common.white, 0.2)
                  : alpha(theme.palette.common.black, 0.2)}`,
              },
              marginLeft: 0,
              width: '100%',
              maxWidth: 720,
              minWidth: 200,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              [currentTheme.breakpoints.up('sm')]: {
                marginLeft: currentTheme.spacing(3),
                width: 'auto',
              },
            }}>
              <SearchIconWrapper>
                <SearchIcon />
              </SearchIconWrapper>
              <StyledInputBase
                placeholder={
                  isEverythingElseRoute 
                    ? "Search food, products, services, experiences..."
                    : UI_TEXT.placeholders.searchServices
                }
                inputProps={{ 'aria-label': UI_TEXT.common.search }}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                autoFocus={searchOpen}
              />
              {searchOpen && isMobile && (
                <IconButton 
                  size="small" 
                  onClick={() => setSearchOpen(false)}
                  sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }}
                  aria-label="Close search"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          )}

          {/* Flexible spacer */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Desktop navigation items */}
          {!isMobile && (
            <Box sx={{ display: 'flex' }}>
              {visibleNavItems.map((item) => (
                <Button
                  key={item.path}
                  component={RouterLink}
                  to={item.path}
                  color={isActiveRoute(location.pathname, item.path, item.exact) ? 'primary' : 'inherit'}
                  sx={{ 
                    mx: 0.5,
                    textTransform: 'none',
                    fontWeight: isActiveRoute(location.pathname, item.path, item.exact) ? 600 : 400,
                  }}
                  aria-current={isActiveRoute(location.pathname, item.path, item.exact) ? 'page' : undefined}
                  startIcon={<item.icon />}
                >
                  {item.badge?.show ? (
                    <Badge 
                      badgeContent={item.badge.value} 
                      color={item.badge.color || 'error'}
                      max={item.badge.max || 99}
                    >
                      {item.label}
                    </Badge>
                  ) : (
                    item.label
                  )}
                </Button>
              ))}
            </Box>
          )}

          {/* Quick search button - Only visible on mobile when search is closed, hidden on Everything Else */}
          {!isEverythingElseRoute && isMobile && !searchOpen && (
            <Tooltip title="Search">
              <IconButton 
                color="inherit" 
                onClick={() => setSearchOpen(true)}
                aria-label="Open search"
                size={isSmall ? 'small' : 'medium'}
                sx={{ ml: { xs: 0.25, sm: 1 } }}
              >
                <SearchIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
              </IconButton>
            </Tooltip>
          )}

          {/* Notifications Menu - only visible when logged in */}
          {user && <NotificationMenu />}

          {/* Messages Menu - only visible when logged in */}
          {user && <MessagesMenu messagesCount={messagesCount} onCountChange={fetchUnreadCount} />}
          
          {/* Location/Currency Indicator - admin only */}
          {user?.isAdmin && (
            <LocationIndicator variant="chip" sx={{ mx: { xs: 0.25, sm: 1 }, display: { xs: 'none', sm: 'flex' } }} />
          )}
          
          {/* Theme toggle with animation */}
          <Tooltip title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}>
            <IconButton 
              color="inherit" 
              onClick={toggleTheme} 
              aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
              size={isSmall ? 'small' : 'medium'}
              sx={{ ml: { xs: 0.25, sm: 1 } }}
            >
              <Zoom in={isDarkMode} timeout={300} unmountOnExit>
                <Box position="absolute">
                  <Brightness7Icon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </Box>
              </Zoom>
              <Zoom in={!isDarkMode} timeout={300} unmountOnExit>
                <Box position="absolute">
                  <Brightness4Icon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </Box>
              </Zoom>
            </IconButton>
          </Tooltip>

          {/* User Menu or Login/Register buttons */}
          {user ? (
            <>
              <Tooltip title="Account settings">
                <IconButton
                  onClick={handleUserMenuOpen}
                  size="small"
                  sx={{ ml: 2 }}
                  aria-controls={Boolean(userMenuAnchorEl) ? 'user-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={Boolean(userMenuAnchorEl) ? 'true' : undefined}
                >
                  <Avatar 
                    sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}
                    alt={user.name || 'User'}
                    src={user.avatar}
                  >
                    {!user.avatar && (user.name ? user.name.charAt(0).toUpperCase() : 'U')}
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Menu
                id="user-menu"
                anchorEl={userMenuAnchorEl}
                open={Boolean(userMenuAnchorEl)}
                onClose={handleUserMenuClose}
                onClick={handleUserMenuClose}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    overflow: 'visible',
                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                    mt: 1.5,
                    '& .MuiAvatar-root': {
                      width: 32,
                      height: 32,
                      ml: -0.5,
                      mr: 1,
                    },
                    '&:before': {
                      content: '""',
                      display: 'block',
                      position: 'absolute',
                      top: 0,
                      right: 14,
                      width: 10,
                      height: 10,
                      bgcolor: 'background.paper',
                      transform: 'translateY(-50%) rotate(45deg)',
                      zIndex: 0,
                    },
                  },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MenuItem component={RouterLink} to="/profile">
                  <Avatar src={user.avatar}>
                    {!user.avatar && (user.name ? user.name.charAt(0).toUpperCase() : 'U')}
                  </Avatar>
                  <Box sx={{ ml: 1 }}>
                    <Typography variant="subtitle2" component="span" display="block">
                      {user.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="span">
                      View your profile
                    </Typography>
                  </Box>
                </MenuItem>
                <Divider />
                <MenuItem component={RouterLink} to="/profile">
                  <ListItemIcon>
                    <PersonIcon fontSize="small" />
                  </ListItemIcon>
                  Profile
                </MenuItem>
                <MenuItem component={RouterLink} to="/settings">
                  <ListItemIcon>
                    <SettingsIcon fontSize="small" />
                  </ListItemIcon>
                  Settings
                </MenuItem>
                {((user as any).subscriptionTier === 'pro' || (user as any).subscriptionTier === 'business_pro') && (
                  <MenuItem component={RouterLink} to="/analytics">
                    <ListItemIcon>
                      <AnalyticsIcon fontSize="small" sx={{ color: 'warning.main' }} />
                    </ListItemIcon>
                    Analytics
                  </MenuItem>
                )}
                {(user.isAdmin || user.role === 'admin') && (
                  <MenuItem 
                    component={RouterLink} 
                    to="/admin"
                  >
                    <ListItemIcon>
                      <AdminIcon fontSize="small" />
                    </ListItemIcon>
                    Admin Dashboard
                  </MenuItem>
                )}
                <Divider />
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Box sx={{ display: 'flex', ml: 1, alignItems: 'center' }}>
              {/* Mobile: Single compact login button */}
              <Button 
                variant="contained"
                color="primary"
                onClick={openLogin}
                size="small"
                sx={{ 
                  display: { xs: 'flex', sm: 'none' },
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 2,
                  py: 0.75,
                  fontSize: '0.875rem'
                }}
              >
                Log In
              </Button>
              {/* Desktop: Login and Sign Up buttons */}
              <Button 
                color="inherit"
                onClick={openLogin}
                sx={{ 
                  display: { xs: 'none', sm: 'flex' },
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                Log In
              </Button>
              <Button 
                variant="contained"
                color="primary"
                onClick={openRegister}
                sx={{ 
                  ml: 1,
                  display: { xs: 'none', sm: 'flex' },
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2
                }}
              >
                Sign Up
              </Button>
            </Box>
          )}
      </Toolbar>

        {/* Mobile Section Tabs — inside AppBar so they're part of the fixed header */}
        {isMobile && sectionContext && (
          <SectionTabs />
        )}
      </AppBar>
  );
};

export default Header;

